import { useState, useEffect } from "react";
import {
  FormControl, InputLabel, MenuItem, Select, Button, FormControlLabel, FormGroup, Checkbox, Card, CardContent, Paper, Box, Grid, Typography, Alert, AlertTitle,
  CircularProgress, Chip, Stack, Fade, Zoom, Tooltip, Collapse, useTheme, alpha,
  Tabs,
  Tab,
} from "@mui/material";
import {
  ContentPasteSearchOutlined as SearchIcon, Download as DownloadIcon, CalendarMonth as CalendarIcon, Dns as MachineIcon, FilterAlt as FilterIcon,
  Info as InfoIcon, Warning as WarningIcon, CheckCircle as CheckCircleIcon, Schedule as ScheduleIcon, TrendingUp as TrendingUpIcon, People as PeopleIcon,
  Assignment as AssignmentIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, LineChart, Line,
} from "recharts";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function ViewCompletedOperations() {
  const theme = useTheme();
  const defaultStartTime = dayjs().hour(8).minute(0);
  const defaultEndTime = dayjs().hour(8).minute(0);

  // Filter States
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedMachine, setSelectedMachine] = useState("");
  const [startTime, setStartTime] = useState(defaultStartTime);
  const [endTime, setEndTime] = useState(defaultEndTime);
  const [ongoingChecked, setOngoingChecked] = useState(true);
  const [finishedChecked, setFinishedChecked] = useState(true);

  // Data States
  const [machineGroups, setMachineGroups] = useState([]);
  const [machines, setMachines] = useState([]);
  const [operationLogs, setOperationLogs] = useState([]);
  const [shiftLogs, setShiftLogs] = useState([]);
  const [jobSummary, setJobSummary] = useState([]);
  const [manSummaryLogs, setManSummaryLogs] = useState([]);
  const [manShiftLogs, setManShiftLogs] = useState([]);
  const [manDetailLogs, setManDetailLogs] = useState([]);
  const [selectedMachineDescription, setSelectedMachineDescription] = useState("");

  // UI States
  const [errorMessage, setErrorMessage] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showFilters, setShowFilters] = useState(true);


  const [jobSummaryGrouped, setJobSummaryGrouped] = useState([]);
  const [manDetailLogsNonApproved, setManDetailLogsNonApproved] = useState([]);

  const [selectedChartTab, setSelectedChartTab] = useState('timeDistribution');

  // UI States

  // Add these new states for table expansion
  const [expandedTables, setExpandedTables] = useState({
    nonApprovedDetails: false,
    approvedDetails: false,
    shiftLogs: false,
    jobSummary: false,
    machineOperations: false,
    machineShiftLogs: false,
  });




  // Toggle function for table expansion
  const toggleTableExpansion = (tableName) => {
    setExpandedTables(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
  };

  // Scroll to a specific table
  const scrollToTable = (tableId) => {
    const element = document.getElementById(tableId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Handle Non-Approved card click
  const handleNonApprovedCardClick = () => {
    // Expand the table if it's collapsed
    if (!expandedTables.nonApprovedDetails) {
      setExpandedTables(prev => ({
        ...prev,
        nonApprovedDetails: true
      }));
    }
    // Scroll to the table after a short delay to allow expansion
    setTimeout(() => {
      scrollToTable('non-approved-table');
    }, 100);
  };

  const getNonApprovedCount = () => {
    return manDetailLogsNonApproved.length;
  };

  const getNonApprovedTotalHours = () => {
    return manDetailLogsNonApproved.reduce((sum, l) => sum + (parseFloat(l.duration_hours) || 0), 0).toFixed(2);
  };


  function timeToDecimalHours(timeStr) {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    return (hours + minutes / 60 + seconds / 3600).toFixed(2);
  }

  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    if (typeof timeStr === 'number') return timeStr;

    // Handle HH:MM:SS format
    const parts = String(timeStr).split(':');
    if (parts.length === 3) {
      const [hours, minutes, seconds] = parts.map(Number);
      return (hours * 60 + minutes + seconds / 60).toFixed(2);
    }

    // If it's already a number string, return it
    return parseFloat(timeStr) || 0;
  };


  const timeValueToHours = (value) => {
    if (!value) return 0;
    if (typeof value === "number") return value;
    const parts = String(value).split(":").map(Number);
    if (parts.length === 3 && parts.every(Number.isFinite)) {
      return parts[0] + parts[1] / 60 + parts[2] / 3600;
    }
    return parseFloat(value) || 0;
  };

  const calculateNonCommandedHours = (row) => {
    if (!row || row.operation_description !== "JOB_STARTED") return 0;
    const totalHours =
      parseFloat(row.duration_hours) || timeValueToHours(row.duration);
    const categorizedHours =
      timeValueToHours(row.prodtime) +
      timeValueToHours(row.idletime) +
      timeValueToHours(row.mealtime);
    return Math.max(0, totalHours - categorizedHours);
  };

  const isBreakingStation = (stationCode) =>
    String(stationCode || "").trim().toUpperCase().startsWith("BRT");

  const calculateTotalCartonCount = (stationCode, prodQty, upCount) => {
    if (!isBreakingStation(stationCode)) return null;

    const productionQty = Number(prodQty);
    const upsCount = Number(upCount);
    if (!Number.isFinite(productionQty) || !Number.isFinite(upsCount)) return null;

    return productionQty * upsCount;
  };

  const isBreakingReport =
    isBreakingStation(selectedMachine) ||
    manDetailLogs.some((log) => isBreakingStation(log.station_code));

  // Check if a log is job-related (has job data)
  const isJobRelated = (log) => {
    return !!(
      log.job_number &&
      log.customer &&
      log.product &&
      log.main_operation &&
      log.job_started_time
    );
  };

  // Get job-related approved logs
  const getJobRelatedApprovedLogs = () => {
    return manDetailLogs.filter(log => isJobRelated(log));
  };

  // Get non-job-related approved logs (like idle time, meal time, setup without job)
  const getNonJobRelatedApprovedLogs = () => {
    return manDetailLogs.filter(log => !isJobRelated(log));
  };

  // Statistics for job-related data
  const getJobRelatedCount = () => {
    return getJobRelatedApprovedLogs().length;
  };

  const getJobRelatedHours = () => {
    return getJobRelatedApprovedLogs()
      .reduce((sum, l) => sum + (parseFloat(l.duration_hours) || 0), 0)
      .toFixed(2);
  };

  const getJobRelatedProductionQty = () => {
    return getJobRelatedApprovedLogs()
      .reduce((sum, l) => sum + (parseFloat(l.prod_qty) || 0), 0);
  };

  // Statistics for non-job-related data
  const getNonJobRelatedCount = () => {
    return getNonJobRelatedApprovedLogs().length;
  };

  const getNonJobRelatedHours = () => {
    return getNonJobRelatedApprovedLogs()
      .reduce((sum, l) => sum + (parseFloat(l.duration_hours) || 0), 0)
      .toFixed(2);
  };

  // Get breakdown by operation type for non-job activities
  const getNonJobBreakdown = () => {
    const logs = getNonJobRelatedApprovedLogs();
    const breakdown = {
      idle: 0,
      meal: 0,
      setup: 0,
      other: 0
    };

    logs.forEach(log => {
      const hours = parseFloat(log.duration_hours) || 0;
      const desc = (log.operation_description || '').toLowerCase();

      if (desc.includes('idle') || log.idletime) {
        breakdown.idle += hours;
      } else if (desc.includes('meal') || log.mealtime) {
        breakdown.meal += hours;
      } else if (desc.includes('setup') || log.setup_time) {
        breakdown.setup += hours;
      } else {
        breakdown.other += hours;
      }
    });

    return breakdown;
  };

  // Chart Data Preparation Functions
  const getTimeDistributionData = () => {
    const jobRelatedHours = parseFloat(getJobRelatedHours());
    const nonJobRelatedHours = parseFloat(getNonJobRelatedHours());
    const nonApprovedHours = parseFloat(getNonApprovedTotalHours());

    return [
      { name: 'Job-Related', value: jobRelatedHours, color: '#4caf50' },
      { name: 'Non-Job Activities', value: nonJobRelatedHours, color: '#ff9800' },
      { name: 'Non-Approved', value: nonApprovedHours, color: '#f44336' },
    ].filter(item => item.value > 0);
  };

  const getNonJobActivityBreakdownData = () => {
    const breakdown = getNonJobBreakdown();
    return [
      { name: 'Idle Time', hours: parseFloat(breakdown.idle.toFixed(2)), color: '#ef5350' },
      { name: 'Meal Time', hours: parseFloat(breakdown.meal.toFixed(2)), color: '#42a5f5' },
      { name: 'Setup Time', hours: parseFloat(breakdown.setup.toFixed(2)), color: '#ab47bc' },
      { name: 'Other', hours: parseFloat(breakdown.other.toFixed(2)), color: '#78909c' },
    ].filter(item => item.hours > 0);
  };

  const getEmployeeProductivityData = () => {
    if (manDetailLogs.length === 0) return [];

    const employeeMap = new Map();

    manDetailLogs.forEach(log => {
      const empKey = `${log.emp_name} (${log.emp_id})`;

      if (!employeeMap.has(empKey)) {
        employeeMap.set(empKey, {
          name: empKey,
          jobHours: 0,
          nonJobHours: 0,
          totalHours: 0,
          prodQty: 0,
        });
      }

      const emp = employeeMap.get(empKey);
      const hours = parseFloat(log.duration_hours) || 0;
      emp.totalHours += hours;

      if (isJobRelated(log)) {
        emp.jobHours += hours;
        emp.prodQty += parseFloat(log.prod_qty) || 0;
      } else {
        emp.nonJobHours += hours;
      }
    });

    return Array.from(employeeMap.values())
      .sort((a, b) => b.totalHours - a.totalHours)
      .slice(0, 10); // Top 10 employees
  };

  const getJobStatusDistributionData = () => {
    if (manDetailLogs.length === 0) return [];

    const statusMap = new Map();

    getJobRelatedApprovedLogs().forEach(log => {
      const status = log.job_status || 'Unknown';
      if (!statusMap.has(status)) {
        statusMap.set(status, { name: status, count: 0, hours: 0 });
      }
      const item = statusMap.get(status);
      item.count += 1;
      item.hours += parseFloat(log.duration_hours) || 0;
    });

    return Array.from(statusMap.values());
  };

  const getProductionQtyByJobData = () => {
    if (jobSummaryGrouped.length === 0) return [];

    return jobSummaryGrouped
      .filter(job => job.totalProdQty > 0)
      .map(job => ({
        name: job.jobNumber.length > 15 ? job.jobNumber.substring(0, 15) + '...' : job.jobNumber,
        fullName: job.jobNumber,
        prodQty: parseInt(job.totalProdQty) || 0,
        wasteQty: parseInt(job.totalWasteQty) || 0,
        orderQty: parseInt(job.totalOrderQty) || 0,
      }))
      .sort((a, b) => b.prodQty - a.prodQty)
      .slice(0, 10); // Top 10 jobs
  };

  const getHourlyTimelineData = () => {
    if (manDetailLogs.length === 0) return [];

    const hourMap = new Map();

    manDetailLogs.forEach(log => {
      if (!log.start_datetime) return;

      const hour = dayjs(log.start_datetime).format('YYYY-MM-DD HH:00');

      if (!hourMap.has(hour)) {
        hourMap.set(hour, {
          time: hour,
          displayTime: dayjs(log.start_datetime).format('MMM DD, HH:00'),
          jobHours: 0,
          nonJobHours: 0,
          records: 0,
        });
      }

      const item = hourMap.get(hour);
      const hours = parseFloat(log.duration_hours) || 0;
      item.records += 1;

      if (isJobRelated(log)) {
        item.jobHours += hours;
      } else {
        item.nonJobHours += hours;
      }
    });

    return Array.from(hourMap.values()).sort((a, b) => a.time.localeCompare(b.time));
  };


  // Custom Tooltip Components

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const entry = payload[0];
      const isJobRelated = entry.name === 'Job-Related';
      const isNonJobRelated = entry.name === 'Non-Job Activities';

      return (
        <Paper sx={{ p: 2, border: '1px solid #ccc', minWidth: 250, maxWidth: 350 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: entry.payload.color }}>
            {entry.name}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
            Total Hours: <strong>{entry.value.toFixed(2)}</strong>
          </Typography>

          {/* Job-Related Details */}
          {isJobRelated && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#4caf50' }}>
                Job-Related Activities
              </Typography>
              <Stack spacing={0.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total Records:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {getJobRelatedCount()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Production Qty:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {getJobRelatedProductionQty()}
                  </Typography>
                </Box>
                <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed #e0e0e0' }}>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                    Records with Job Number, Customer, Product & Main Operation
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Non-Job-Related Details */}
          {isNonJobRelated && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#ff9800' }}>
                Non-Job Activities
              </Typography>
              <Stack spacing={0.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total Records:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {getNonJobRelatedCount()}
                  </Typography>
                </Box>

                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Breakdown:
                  </Typography>
                  {getNonJobBreakdown().idle > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        • Idle Time:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        {getNonJobBreakdown().idle.toFixed(2)} hrs
                      </Typography>
                    </Box>
                  )}
                  {getNonJobBreakdown().meal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        • Meal Time:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        {getNonJobBreakdown().meal.toFixed(2)} hrs
                      </Typography>
                    </Box>
                  )}
                  {getNonJobBreakdown().setup > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        • Setup Time:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        {getNonJobBreakdown().setup.toFixed(2)} hrs
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed #e0e0e0' }}>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                    Activities without job assignment
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}
        </Paper>
      );
    }
    return null;
  };

  // Standard Tooltip for OTHER CHARTS (bar, line, etc.)
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5, border: '1px solid #ccc' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="caption" sx={{ color: entry.color, display: 'block' }}>
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };



  const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Don't show label if less than 5%

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontWeight: 600, fontSize: '14px' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const detailColumns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "supervisor_id", headerName: "Supervisor ID", width: 130 },
    { field: "supervisor_name", headerName: "Supervisor Name", width: 180 },
    { field: "emp_id", headerName: "Employee ID", width: 130 },
    { field: "emp_name", headerName: "Employee Name", width: 180 },
    { field: "station_code", headerName: "Station Code", width: 150 },
    { field: "job_number", headerName: "Job Number", width: 150 },
    { field: "customer", headerName: "Customer", width: 180 },
    { field: "product", headerName: "Product", width: 180 },
    { field: "main_operation", headerName: "Main Operation", width: 180 },
    { field: "operation_code", headerName: "Operation Code", width: 150 },
    { field: "operation_description", headerName: "Operation Description", width: 200 },
    // Replace the datetime columns in detailColumns (around lines 97-115) with this fixed version:

    {
      field: "start_datetime",
      headerName: "Start Time",
      width: 200,
      valueFormatter: (params) => {
        // Handle both old and new DataGrid API
        const value = params?.value || params;
        if (!value) return "";
        return dayjs(value).format("YYYY-MM-DD HH:mm:ss");
      },
    },
    {
      field: "end_datetime",
      headerName: "End Time",
      width: 200,
      valueFormatter: (params) => {
        const value = params?.value || params;
        if (!value) return "";
        return dayjs(value).format("YYYY-MM-DD HH:mm:ss");
      },
    },
    {
      field: "job_started_time",
      headerName: "Job Started Time",
      width: 200,
      valueFormatter: (params) => {
        const value = params?.value || params;
        if (!value) return "";
        return dayjs(value).format("YYYY-MM-DD HH:mm:ss");
      },
    },
    { field: "remarks", headerName: "Remarks", width: 250 },
    { field: "operation_type", headerName: "Operation Type", width: 150 },

    // Replace the duration columns section (around lines 120-180) with this fixed version:

    {
      field: "duration_minutes",
      headerName: "Duration (Minutes)",
      width: 160,
      valueGetter: (params) => {
        if (!params || !params.row) return 0;
        return parseFloat(params.row.duration_minutes || 0).toFixed(2);
      },
    },
    {
      field: "duration_hours",
      headerName: "Duration (Hours)",
      width: 160,
      valueGetter: (params) => {
        if (!params || !params.row) return 0;
        return parseFloat(params.row.duration_hours || 0).toFixed(2);
      },
    },
    {
      field: "prodtime",
      headerName: "Prod Time (hours)",
      width: 150,
      valueGetter: (params) => {
        if (!params || !params.row || !params.row.prodtime) return 0;
        const mins = timeToMinutes(params.row.prodtime);
        return (parseFloat(mins) / 60).toFixed(2);
      },
    },
    {
      field: "idletime",
      headerName: "Idle Time (hours)",
      width: 150,
      valueGetter: (params) => {
        if (!params || !params.row || !params.row.idletime) return 0;
        const mins = timeToMinutes(params.row.idletime);
        return (parseFloat(mins) / 60).toFixed(2);
      },
    },
    {
      field: "mealtime",
      headerName: "Meal Time (hours)",
      width: 150,
      valueGetter: (params) => {
        if (!params || !params.row || !params.row.mealtime) return 0;
        const mins = timeToMinutes(params.row.mealtime);
        return (parseFloat(mins) / 60).toFixed(2);
      },
    },
    {
      field: "non_commanded_hours",
      headerName: "Non-commanded (hours)",
      width: 180,
      valueGetter: (params) => {
        if (!params || !params.row) return 0;
        return parseFloat(params.row.non_commanded_hours || 0).toFixed(2);
      },
    },
    {
      field: "setup_time",
      headerName: "Setup Time (hours)",
      width: 150,
      valueGetter: (params) => {
        if (!params || !params.row || !params.row.setup_time) return 0;
        const mins = timeToMinutes(params.row.setup_time);
        return (parseFloat(mins) / 60).toFixed(2);
      },
    },
    { field: "order_qty", headerName: "Order Qty", width: 120 },
    {
      field: "up_count",
      headerName: "Ups Count",
      width: 110,
      valueFormatter: (params) => {
        const value = params?.value ?? params;
        return value === null || value === undefined || value === "" ? "—" : value;
      },
    },
    { field: "prod_qty", headerName: "Production Qty", width: 150 },
    ...(isBreakingReport ? [{
      field: "total_carton_count",
      headerName: "Total Carton Count",
      width: 170,
      valueGetter: (params) =>
        calculateTotalCartonCount(
          params?.row?.station_code,
          params?.row?.prod_qty,
          params?.row?.up_count
        ) ?? "",
    }] : []),
    { field: "waste_qty", headerName: "Waste Qty", width: 130 },
    { field: "finished_good_status", headerName: "Finished Good Status", width: 180 },
    { field: "job_status", headerName: "Job Status", width: 130 },
    {
      field: "is_approved",
      headerName: "Is Approved",
      width: 120,
      valueFormatter: (params) => {
        if (params && params.value) return "Yes";
        return "No";
      },
    },
    {
      field: "approved_at",
      headerName: "Approved At",
      width: 180,
      valueFormatter: (params) => {
        if (!params || !params.value) return "";
        return dayjs(params.value).format("YYYY-MM-DD HH:mm:ss");
      },
    },
    { field: "idle_reasons", headerName: "Idle Reasons", width: 200 },
    {
      field: "created_at",
      headerName: "Created At",
      width: 180,
      valueFormatter: (params) => {
        if (!params || !params.value) return "";
        return dayjs(params.value).format("YYYY-MM-DD HH:mm:ss");
      },
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      width: 180,
      valueFormatter: (params) => {
        if (!params || !params.value) return "";
        return dayjs(params.value).format("YYYY-MM-DD HH:mm:ss");
      },
    },
  ];

  const shiftColumns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "supervisor", headerName: "Supervisor", width: 150 },
    { field: "employee", headerName: "Employee", width: 150 },
    { field: "stationCode", headerName: "Station Code", width: 150 },
    { field: "shiftIn", headerName: "Shift In", width: 150 },
    { field: "shiftOut", headerName: "Shift Out", width: 150 },
    { field: "activeStatus", headerName: "Active Status", width: 150 },
  ];

  const summaryColumns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "jobNumber", headerName: "Job Number", width: 150 },
    { field: "customer", headerName: "Customer", width: 150 },
    { field: "product", headerName: "Product", width: 180 },
    { field: "mainOperation", headerName: "Main Operation", width: 180 },
    { field: "workstationCode", headerName: "Workstation", width: 130 },
    { field: "employees", headerName: "Employees", width: 250 },
    { field: "employeeCount", headerName: "Employee Count", width: 130 },
    { field: "totalOrderQty", headerName: "Total Order Qty", width: 140 },
    { field: "upCounts", headerName: "Ups Count", width: 120 },
    { field: "totalProdQty", headerName: "Total Prod Qty", width: 140 },
    ...(isBreakingReport ? [{
      field: "totalCartonCount",
      headerName: "Total Carton Count",
      width: 170,
    }] : []),
    { field: "totalWasteQty", headerName: "Total Waste Qty", width: 140 },
    { field: "totalDurationHours", headerName: "Total Duration (hrs)", width: 160 },
    { field: "totalSetupHours", headerName: "Total Setup (hrs)", width: 150 },
    { field: "totalIdleHours", headerName: "Total Idle (hrs)", width: 150 },
    { field: "totalProdHours", headerName: "Total Prod (hrs)", width: 150 },
    { field: "totalMealHours", headerName: "Total Meal (hrs)", width: 150 },
    { field: "totalNonCommandedHours", headerName: "Non-commanded (hrs)", width: 180 },
    { field: "finishedGood", headerName: "Finished Good", width: 130 },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "earliestJobStart",
      headerName: "Earliest Start",
      width: 180,
      valueFormatter: (params) => {
        if (!params || !params.value) return "";
        return dayjs(params.value).format("YYYY-MM-DD HH:mm:ss");
      },
    },
    {
      field: "latestJobEnd",
      headerName: "Latest End",
      width: 180,
      valueFormatter: (params) => {
        if (!params || !params.value) return "";
        return dayjs(params.value).format("YYYY-MM-DD HH:mm:ss");
      },
    },
    { field: "idleReasons", headerName: "Idle Reasons", width: 200 },
  ];


  // Fetch Machine Groups
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/MachineGroupCodeWithNames`)
      .then((response) => {
        setMachineGroups(response.data);
      })
      .catch((error) => console.error("Error fetching machine groups:", error));
  }, []);

  // Handle Group Change
  const handleGroupChange = (event) => {
    const group = event.target.value;
    setSelectedGroup(group);
    setSelectedMachine("");
    fetchMachines(group);
  };

  // Fetch Machines
  const fetchMachines = (groupCode) => {
    axios
      .get(`${API_BASE_URL}/MachineCodeWithNames?groupCode=${groupCode}`)
      .then((response) => {
        setMachines(response.data);
      })
      .catch((error) => console.error("Error fetching machines:", error));
  };

  // Handle Machine Change
  const handleMachineChange = (event) => {
    const machineCode = event.target.value;
    setSelectedMachine(machineCode);
    const selectedMachine = machines.find((machine) => machine.MachineCode === machineCode);
    if (selectedMachine) {
      setSelectedMachineDescription(selectedMachine.MachineDescription);
    }
  };

  // Validate Time Range
  const validateTimeRange = (start, end) => {
    if (start && end) {
      const timeDiff = dayjs(end).diff(start, "minute");
      if (timeDiff <= 0) {
        setErrorMessage("Please select a valid Date-time range.");
      } else {
        setErrorMessage("");
      }
    }
  };

  // Handle View Click
  const handleViewClick = async () => {
    if (!startTime || !selectedMachine) {
      setErrorMessage("Please ensure all required fields are selected.");
      return;
    }

    const timeDiff = dayjs(endTime).diff(startTime, "minute");
    if (timeDiff <= 0) {
      setErrorMessage("End time must be after start time.");
      return;
    }

    setIsSearching(true);
    setErrorMessage("");
    setOperationLogs([]);
    setShiftLogs([]);
    setJobSummary([]);
    setManShiftLogs([]);
    setManDetailLogs([]);
    setManSummaryLogs([]);
    setJobSummaryGrouped([]);

    const selectedMachineDetails = machines.find((machine) => machine.MachineCode === selectedMachine);

    try {
      if (selectedMachineDetails?.HoursType === "Man") {
        await Promise.all([fetchManShiftLog(), fetchManDetailLog()]);
      } else {
        await Promise.all([fetchOperationLogs(), fetchShiftlog(), fetchJobsummary()]);
      }
    } catch (error) {
      setErrorMessage("Failed to fetch data. Please try again.");
      console.error("Error fetching data:", error);
    } finally {
      setIsSearching(false);
    }
  };

  // Fetch Functions
  const fetchManShiftLog = async () => {
    const response = await axios.post(`${API_BASE_URL}/ViewManShiftLog`, {
      startTime: startTime.format("YYYY-MM-DD HH:mm:ss"),
      endTime: endTime.format("YYYY-MM-DD HH:mm:ss"),
      machineCode: selectedMachine,
      loggedInChecked: ongoingChecked,
      loggedOutChecked: finishedChecked,
    });

    const adjustedManShiftLogs = response.data.map((shift) => ({
      id: shift.id,
      supervisor: `${shift.supervisor_name || "Supervisor"} (${shift.supervisor_id})`,
      employee: `${shift.emp_name} (${shift.emp_id})`,
      stationCode: shift.station_code,
      shiftIn: dayjs(shift.shift_in_datetime).format("YYYY-MM-DD HH:mm:ss"),
      shiftOut: shift.shift_out_datetime ? dayjs(shift.shift_out_datetime).format("YYYY-MM-DD HH:mm:ss") : null,
      activeStatus: shift.active_status,
    }));

    setManShiftLogs(adjustedManShiftLogs);
  };

  const fetchManDetailLog = () => {
    console.log("=== FETCHING MAN DETAIL LOG ===");
    axios.post(`${API_BASE_URL}/ViewManDetailLog`, {
      startTime: startTime.format("YYYY-MM-DD HH:mm:ss"),
      endTime: endTime.format("YYYY-MM-DD HH:mm:ss"),
      machineCode: selectedMachine,
    })
      .then((res) => {
        console.log("✓ API Response received, rows:", res.data.length);

        const normalized = res.data.map(row => ({
          id: row.id,
          supervisor_id: row.supervisor_id,
          supervisor_name: row.supervisor_name,
          emp_id: row.emp_id,
          emp_name: row.emp_name,
          station_code: row.station_code,
          job_number: row.job_number,
          customer: row.customer,
          product: row.product,
          main_operation: row.main_operation,
          up_count: row.operation_description === "JOB_STARTED" ? row.up_count : null,
          operation_code: row.operation_code,
          operation_description: row.operation_description,
          start_datetime: row.start_datetime,
          end_datetime: row.end_datetime,
          remarks: row.remarks,
          operation_type: row.operation_type,
          duration_minutes: row.duration_minutes,
          duration_hours: row.duration_hours,
          duration: row.duration,
          job_started_time: row.job_started_time,
          prod_qty: row.prod_qty,
          waste_qty: row.waste_qty,
          order_qty: row.order_qty,
          finished_good_status: row.finished_good_status,
          job_status: row.job_status,
          is_approved: row.is_approved,
          approved_at: row.approved_at,
          prodtime: row.prodtime,
          idletime: row.idletime,
          mealtime: row.mealtime,
          non_commanded_hours: Number(calculateNonCommandedHours(row).toFixed(2)),
          setup_time: row.setup_time,
          idle_reasons: row.idle_reasons,
          created_at: row.created_at,
          updated_at: row.updated_at
        }));

        console.log("✓ Data normalized");

        // Separate approved and non-approved logs
        const approvedLogs = normalized.filter(log => log.is_approved === true || log.is_approved === 1);
        const nonApprovedLogs = normalized.filter(log => log.is_approved === false || log.is_approved === 0);

        console.log("✓ Approved logs:", approvedLogs.length);
        console.log("✓ Non-approved logs:", nonApprovedLogs.length);

        setManDetailLogs(approvedLogs);
        setManDetailLogsNonApproved(nonApprovedLogs);

        // Generate Employee-wise Job Summary from APPROVED JOB_STARTED records only
        try {
          const employeeSummary = generateEmployeeWiseJobSummary(approvedLogs);
          setManSummaryLogs(employeeSummary);
          console.log("✓ Employee-wise Job Summary generated:", employeeSummary.length, "records");

          // Generate Job Summary (Grouped) from Employee-wise Summary
          const groupedSummary = generateJobSummary(employeeSummary);
          setJobSummaryGrouped(groupedSummary);
          console.log("✓ Job Summary (Grouped) generated:", groupedSummary.length, "records");
        } catch (error) {
          console.error("✗ Error generating summaries:", error);
        }

        console.log("=== FETCH COMPLETE ===");
      })
      .catch((err) => {
        console.error("✗ Error fetching MAN detail logs:", err);
      });
  };



  const fetchOperationLogs = async () => {
    const response = await axios.post(`${API_BASE_URL}/ViewOperationlog`, {
      startTime: startTime.format("YYYY-MM-DD HH:mm:ss"),
      endTime: endTime.format("YYYY-MM-DD HH:mm:ss"),
      machineCode: selectedMachine,
      ongoingChecked,
      finishedChecked,
    });
    
    // Adjust dates and remarks format (matching old code behavior)
    const adjustedLogs = response.data.map((log, index) => {
      // Handle ISO format dates from backend
      let adjustedStartTime = "";
      let adjustedEndTime = "";
      
      if (log.start_datetime) {
        const startDate = dayjs(log.start_datetime);
        adjustedStartTime = startDate.isValid() ? startDate.format("YYYY-MM-DD HH:mm:ss") : log.start_datetime;
      }
      
      if (log.end_datetime) {
        const endDate = dayjs(log.end_datetime);
        adjustedEndTime = endDate.isValid() ? endDate.format("YYYY-MM-DD HH:mm:ss") : log.end_datetime;
      }
      
      const adjustedRemarks = log.remarks ? log.remarks.replace(/, /g, ',\n') : '';
      
      return { 
        ...log, 
        id: log.id || index + 1,
        start_datetime: adjustedStartTime, 
        end_datetime: adjustedEndTime, 
        remarks: adjustedRemarks 
      };
    });

    console.log("Sample log before adjustment:", response.data[0]);
    console.log("Sample log after adjustment:", adjustedLogs[0]);
    setOperationLogs(adjustedLogs);
  };

  const fetchShiftlog = async () => {
    const response = await axios.post(`${API_BASE_URL}/ViewShiftlog`, {
      startTime: startTime.format("YYYY-MM-DD HH:mm:ss"),
      endTime: endTime.format("YYYY-MM-DD HH:mm:ss"),
      machineCode: selectedMachine,
      loggedInChecked: ongoingChecked,
      loggedOutChecked: finishedChecked,
    });
    const logsWithId = response.data.map((log, index) => ({
      ...log,
      id: log.id || index + 1,
    }));

    setShiftLogs(logsWithId);
  };

  const fetchJobsummary = async () => {
    const response = await axios.post(`${API_BASE_URL}/Viewjobsummary`, {
      startTime: startTime.format("YYYY-MM-DD HH:mm:ss"),
      endTime: endTime.format("YYYY-MM-DD HH:mm:ss"),
      machineCode: selectedMachine,
      InProgressChecked: ongoingChecked,
      FinishedChecked: finishedChecked,
    });
    setJobSummary(response.data);
  };


  const generateEmployeeWiseJobSummary = (detailLogs) => {
    console.log("=== Generating Employee-wise Job Summary ===");

    // Filter only JOB_STARTED records
    const jobStartedLogs = detailLogs.filter(log => log.operation_description === "JOB_STARTED");

    console.log("JOB_STARTED records found:", jobStartedLogs.length);

    // Map to employee-wise summary format
    const employeeWiseSummary = jobStartedLogs.map((log, index) => ({
      id: index + 1,
      supervisor: `${log.supervisor_name || "Supervisor"} (${log.supervisor_id})`,
      employee: `${log.emp_name} (${log.emp_id})`,
      workstationCode: log.station_code,
      jobNumber: log.job_number,
      customer: log.customer,
      product: log.product,
      mainOperation: log.main_operation,
      orderQty: log.order_qty || 0,
      upCount: log.up_count ?? null,
      prodQty: log.prod_qty || 0,
      totalCartonCount: calculateTotalCartonCount(
        log.station_code,
        log.prod_qty,
        log.up_count
      ),
      wasteQty: log.waste_qty || 0,
      finishedGood: log.finished_good_status,
      jobStarted: log.job_started_time || log.start_datetime,
      jobEnded: log.end_datetime,
      duration: log.duration,
      durationHours: log.duration_hours,
      totalSetup: log.setup_time || "0:00:00",
      totalIdle: log.idletime || "0:00:00",
      totalProd: log.prodtime || "0:00:00",
      totalMeals: log.mealtime || "0:00:00",
      nonCommandedHours: Number((parseFloat(log.non_commanded_hours) || 0).toFixed(2)),
      idleReasons: log.idle_reasons || "",
      status: log.job_status,
      remarks: log.remarks || "",
    }));

    console.log("Employee-wise Job Summary records:", employeeWiseSummary.length);
    return employeeWiseSummary;
  };

  const generateJobSummary = (employeeWiseSummary) => {
    console.log("=== Generating Job Summary (Grouped) ===");

    if (!employeeWiseSummary || employeeWiseSummary.length === 0) {
      console.log("No employee-wise summary data to group");
      return [];
    }

    // Group by Job Number + Customer + Product + Main Operation
    const groupMap = new Map();

    for (const log of employeeWiseSummary) {
      // Create unique key for grouping
      const key = `${log.jobNumber}||${log.customer}||${log.product}||${log.mainOperation}`;

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          jobNumber: log.jobNumber,
          customer: log.customer,
          product: log.product,
          mainOperation: log.mainOperation,
          workstationCode: log.workstationCode,
          finishedGood: log.finishedGood,
          status: log.status,
          employees: new Set(),
          upCounts: new Set(),
          totalOrderQty: 0,
          totalProdQty: 0,
          totalCartonCount: 0,
          totalWasteQty: 0,
          totalDurationHours: 0,
          totalSetupTime: 0,
          totalIdleTime: 0,
          totalProdTime: 0,
          totalMealTime: 0,
          totalNonCommandedTime: 0,
          earliestJobStart: log.jobStarted,
          latestJobEnd: log.jobEnded,
          allIdleReasons: new Set(),
          allRemarks: new Set(),
        });
      }

      const group = groupMap.get(key);

      // Add employee to set (unique employees working on this job)
      group.employees.add(log.employee);
      if (log.upCount !== null && log.upCount !== undefined && log.upCount !== "") {
        group.upCounts.add(Number(log.upCount));
      }

      // Sum quantities
      group.totalOrderQty += parseInt(log.orderQty) || 0;
      group.totalProdQty += parseInt(log.prodQty) || 0;
      group.totalCartonCount += parseInt(log.totalCartonCount) || 0;
      group.totalWasteQty += parseInt(log.wasteQty) || 0;

      // Sum duration
      group.totalDurationHours += parseFloat(log.durationHours) || 0;

      // Helper function to convert time string (HH:MM:SS) to hours
      const timeToHours = (timeStr) => {
        if (!timeStr) return 0;
        if (typeof timeStr === 'number') return timeStr;

        // Handle HH:MM:SS format
        const parts = String(timeStr).split(':');
        if (parts.length === 3) {
          const [hours, minutes, seconds] = parts.map(Number);
          return hours + (minutes / 60) + (seconds / 3600);
        }

        // Try parsing as float
        return parseFloat(timeStr) || 0;
      };

      // Sum time values
      group.totalSetupTime += timeToHours(log.totalSetup);
      group.totalIdleTime += timeToHours(log.totalIdle);
      group.totalProdTime += timeToHours(log.totalProd);
      group.totalMealTime += timeToHours(log.totalMeals);
      group.totalNonCommandedTime += parseFloat(log.nonCommandedHours) || 0;

      // Track earliest start and latest end
      if (log.jobStarted) {
        const startDate = new Date(log.jobStarted);
        const currentEarliest = new Date(group.earliestJobStart);
        if (startDate < currentEarliest) {
          group.earliestJobStart = log.jobStarted;
        }
      }

      if (log.jobEnded) {
        const endDate = new Date(log.jobEnded);
        const currentLatest = new Date(group.latestJobEnd);
        if (endDate > currentLatest) {
          group.latestJobEnd = log.jobEnded;
        }
      }

      // Collect idle reasons and remarks
      if (log.idleReasons && log.idleReasons.trim()) {
        group.allIdleReasons.add(log.idleReasons.trim());
      }
      if (log.remarks && log.remarks.trim()) {
        group.allRemarks.add(log.remarks.trim());
      }
    }

    // Convert map to array with formatted data
    const summary = Array.from(groupMap.values()).map((group, index) => ({
      id: index + 1,
      jobNumber: group.jobNumber,
      customer: group.customer,
      product: group.product,
      mainOperation: group.mainOperation,
      workstationCode: group.workstationCode,
      employees: Array.from(group.employees).join(', '),
      employeeCount: group.employees.size,
      totalOrderQty: group.totalOrderQty,
      upCounts: Array.from(group.upCounts).sort((a, b) => a - b).join(", ") || "—",
      totalProdQty: group.totalProdQty,
      totalCartonCount: group.totalCartonCount,
      totalWasteQty: group.totalWasteQty,
      totalDurationHours: group.totalDurationHours.toFixed(2),
      totalSetupHours: group.totalSetupTime.toFixed(2),
      totalIdleHours: group.totalIdleTime.toFixed(2),
      totalProdHours: group.totalProdTime.toFixed(2),
      totalMealHours: group.totalMealTime.toFixed(2),
      totalNonCommandedHours: group.totalNonCommandedTime.toFixed(2),
      finishedGood: group.finishedGood,
      status: group.status,
      earliestJobStart: group.earliestJobStart,
      latestJobEnd: group.latestJobEnd,
      idleReasons: Array.from(group.allIdleReasons).join('; '),
      remarks: Array.from(group.allRemarks).join('; '),
    }));

    console.log("Job Summary (grouped) records:", summary.length);
    return summary;
  };



  const downloadExcel = async () => {
    if (operationLogs.length === 0 && shiftLogs.length === 0) {
      console.error("No logs to download.");
      return;
    }

    const machineCode = selectedMachine;
    const formattedStartTime = startTime.format("YYYYMMDD_HHmmA");
    const formattedEndTime = endTime.format("YYYYMMDD_HHmmA");
    const downloadTime = dayjs().format("YYYYMMDD_HHmmA");
    const filename = `${machineCode}-${formattedStartTime}-${formattedEndTime}-Logs-${downloadTime}.xlsx`;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Operation Logs');

    // Add two rows above the shift log table
    worksheet.addRow([]);
    worksheet.addRow([]);

    // Merge the first six cells in the first row and add the "Operation Detail Report" text
    worksheet.mergeCells('A1:P1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = "Operation Detail Report";
    titleCell.font = { size: 20, bold: true };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '92CDDC' } };

    const MachineCell = worksheet.getCell('A2');
    MachineCell.value = "Machine :";
    MachineCell.font = { size: 11, bold: true };
    MachineCell.alignment = { horizontal: 'right', vertical: 'middle' };

    const Machinename = worksheet.getCell('B2');
    Machinename.value = selectedMachineDescription;
    Machinename.font = { size: 11, bold: true };
    Machinename.alignment = { horizontal: 'left', vertical: 'center' };

    const Timerange = worksheet.getCell('D2');
    Timerange.value = "Time Range :";
    Timerange.font = { size: 11, bold: true };
    Timerange.alignment = { horizontal: 'right', vertical: 'center' };

    const Starttime = worksheet.getCell('E2');
    Starttime.value = `${startTime.format("D MMMM YYYY hh:mm A")} -`;
    Starttime.font = { size: 11, bold: true };
    Starttime.alignment = { horizontal: 'right', vertical: 'center' };

    const Endtime = worksheet.getCell('F2');
    Endtime.value = endTime.format("D MMMM YYYY hh:mm A");
    Endtime.font = { size: 11, bold: true };
    Endtime.alignment = { horizontal: 'left', vertical: 'center' };

    const OngoingOperation = worksheet.getCell('G2');
    OngoingOperation.value = "Ongoing Operations";
    OngoingOperation.font = { size: 11, bold: true };
    OngoingOperation.alignment = { horizontal: 'right', vertical: 'center' };

    const OngoingOperationIcon = worksheet.getCell('H2');
    OngoingOperationIcon.value = ongoingChecked ? { richText: [{ text: "âœ…", font: { size: 11, bold: true, color: { argb: '008000' } } }] } : { richText: [{ text: "âŽ", font: { size: 11, bold: true, color: { argb: 'FF0000' } } }] };
    OngoingOperationIcon.font = { size: 11, bold: true };
    OngoingOperationIcon.alignment = { horizontal: 'left', vertical: 'center' };

    worksheet.addRow([]);

    const FinishedOprations = worksheet.getCell('I2');
    FinishedOprations.value = "Finished Operations";
    FinishedOprations.font = { size: 11, bold: true };
    FinishedOprations.alignment = { horizontal: 'right', vertical: 'center' };

    const FinishedOprationsIcon = worksheet.getCell('J2');
    FinishedOprationsIcon.value = finishedChecked ? { richText: [{ text: "âœ…", font: { size: 11, bold: true, color: { argb: '008000' } } }] } : { richText: [{ text: "âŽ", font: { size: 11, bold: true, color: { argb: 'FF0000' } } }] };
    FinishedOprationsIcon.font = { size: 11, bold: true };
    FinishedOprationsIcon.alignment = { horizontal: 'left', vertical: 'center' };

    // Apply the green background color to cells from D2 to P2
    for (let col = 1; col <= 16; col++) {  // D = 4th column, P = 16th column
      const cell = worksheet.getCell(2, col);
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'CCFFCC' } };
    }

    ///////////////////

    // Add shift log header
    const shiftHeader = [
      "Employee ID", "Employee Name(s)", "Machine Code", "Shift Logged-IN", "Shift Logged-OUT", "Remarks", "", "Active Status"
    ];
    worksheet.addRow(shiftHeader);

    // Merge header cells for "Remarks"
    worksheet.mergeCells('F4:G4');

    worksheet.getRow(4).eachCell(cell => {
      cell.font = { bold: true, color: { argb: '000000' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FABF8F' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'F79646' } },
        left: { style: 'thin', color: { argb: 'F79646' } },
        bottom: { style: 'thin', color: { argb: 'F79646' } },
        right: { style: 'thin', color: { argb: 'F79646' } },
      };
    });

    worksheet.getRow(4).height = 40;

    shiftLogs.forEach((log) => {
      const shiftIn = log.shift_loggedIn_DateTime ? dayjs(log.shift_loggedIn_DateTime).format("YYYY-MM-DD hh:mm A") : "";
      const shiftOut = log.shift_loggedOut_DateTime ? dayjs(log.shift_loggedOut_DateTime).format("YYYY-MM-DD hh:mm A") : "";

      const employeeNames = [
        log.operator ? `${log.operator}[OP]` : "",
        log.AstOperator ? `${log.AstOperator}[AOP]` : "",
        log.Helpers ? `${log.Helpers}[HLP]` : ""
      ].filter(name => name).join(",\n");

      const row = worksheet.addRow([
        log.Employee_ID,
        employeeNames,
        log.machineCode,
        shiftIn,
        shiftOut,
        log.remarks || "", // Adding an extra column for the merged cells
        log.ActiveStatus
      ]);

      // Merge cells in F and G columns for this row
      worksheet.mergeCells(`F${row.number}:G${row.number}`);

      row.eachCell((cell, colNumber) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
        cell.border = {
          top: { style: 'thin', color: { argb: 'F79646' } },
          left: { style: 'thin', color: { argb: 'F79646' } },
          bottom: { style: 'thin', color: { argb: 'F79646' } },
          right: { style: 'thin', color: { argb: 'F79646' } },
        };
        cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };

        if (colNumber === 2) {
          cell.alignment.horizontal = 'left';
        }
      });
    });

    worksheet.getColumn(2).width = 50;

    worksheet.addRow([]); // Add empty row between shiftLogs and operationLogs

    const operationHeader = [
      "Employee ID", "Name", "Machine Code", "Job Number", "Customer", "Product",
      "Main Operation", "Operation Code", "Operation Description", "Operation Type", "Start Datetime",
      "End Datetime", "Duration Minutes", "Duration Hours", "Remarks", "Active Status"
    ];
    worksheet.addRow(operationHeader);

    worksheet.getRow(shiftLogs.length + 6).eachCell(cell => { // Shift logs + header + empty row + title + title row
      cell.font = { bold: true, color: { argb: '000000' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FABF8F' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'F79646' } },
        left: { style: 'thin', color: { argb: 'F79646' } },
        bottom: { style: 'thin', color: { argb: 'F79646' } },
        right: { style: 'thin', color: { argb: 'F79646' } },
      };
    });

    worksheet.getRow(shiftLogs.length + 6).height = 40;

    operationLogs.forEach((log) => {
      const row = worksheet.addRow([
        log.employee_id,
        log.name || "",
        log.machine_code,
        log.job_number,
        log.customer,
        log.product,
        log.main_operation || "",
        log.operation_code || "",
        log.operation_description || "",
        log.operation_type,
        dayjs(log.start_datetime).format("YYYY-MM-DD hh:mm A"),
        dayjs(log.end_datetime).format("YYYY-MM-DD hh:mm A"),
        log.duration_minutes,
        log.duration_hours,
        log.remarks || "",
        log.active_status,
      ]);

      if (log.operation_type !== "JOB_STARTED") {
        // Set the cells for "Job Number", "Customer", "Product", and "Main Operation" as empty
        row.getCell(4).value = ""; // Job Number
        row.getCell(5).value = ""; // Customer
        row.getCell(6).value = ""; // Product
        row.getCell(7).value = ""; // Main Operation
      }

      let rowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }; // Default white
      if (row.getCell(10).value === "JOB_STARTED") {
        rowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'C5D9F1' } };
      } else if (row.getCell(10).value === "JOB_END") {
        rowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFCC' } };
      }

      row.eachCell((cell, colNumber) => {
        cell.fill = rowFill;
        cell.border = {
          top: { style: 'thin', color: { argb: 'F79646' } },
          left: { style: 'thin', color: { argb: 'F79646' } },
          bottom: { style: 'thin', color: { argb: 'F79646' } },
          right: { style: 'thin', color: { argb: 'F79646' } },
        };
        cell.alignment = { wrapText: true, vertical: 'middle' };

        if ([2, 4, 5, 6, 8, 9, 10].includes(colNumber)) {
          cell.alignment.horizontal = 'left';
        }
        if ([13, 14].includes(colNumber)) {
          cell.alignment.horizontal = 'right';
        }
      });

      for (let colNumber = 1; colNumber <= operationHeader.length; colNumber++) {
        if (!row.getCell(colNumber).value) {
          const cell = row.getCell(colNumber);
          cell.fill = rowFill;
          cell.border = {
            top: { style: 'thin', color: { argb: 'F79646' } },
            left: { style: 'thin', color: { argb: 'F79646' } },
            bottom: { style: 'thin', color: { argb: 'F79646' } },
            right: { style: 'thin', color: { argb: 'F79646' } },
          };
          cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
        }
      }


    });

    worksheet.autoFilter = {
      from: { row: 4, column: 1 },
      to: { row: 4, column: shiftHeader.length }
    };

    worksheet.columns.forEach((column, index) => {
      let maxWidth = 8; // Default width

      if (index === 1) { // Employee Name(s) column
        column.eachCell({ includeEmpty: true }, (cell) => {
          const cellValue = cell.value ? cell.value.toString() : '';
          const lines = cellValue.split('\n');
          lines.forEach(line => {
            const cellWidth = line.length;
            if (cellWidth > maxWidth) {
              maxWidth = cellWidth;
            }
          });
        });
      }
      if (index === 3 || index === 4 || index === 5 || index === 8 || index === 9 || index === 10 || index === 11) {
        column.eachCell({ includeEmpty: true }, (cell) => {
          const cellValue = cell.value ? cell.value.toString() : '';
          const cellWidth = cellValue.length;
          if (cellWidth > maxWidth) {
            maxWidth = cellWidth;
          }
        });
      }
      if (index === 14) {
        maxWidth = 30;
      }

      column.width = maxWidth + 1; // Adding padding for better appearance
    });

    // Format cells in 'hours' and 'minutes' columns with numerical values to two decimal places
    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell, colNumber) => {
        if ((worksheet.getColumn(colNumber).key === 'Hours' || worksheet.getColumn(colNumber).key === 'Minutes') && typeof cell.value === 'number') {
          cell.numFmt = '0.00'; // Format numerical values to two decimal places
        }
      });
    });

    worksheet.getColumn(3).hidden = true;
    worksheet.getColumn(7).width = 40; // Set fixed width for column G



    // Second Worksheet: Job Summary
    const jobSummaryWorksheet = workbook.addWorksheet('Job Summary');

    const jobSummaryHeader = [
      "Employee ID",
      "Name",
      "Machine Code",
      "Job Number",
      "Customer",
      "Product",
      "Main Operation",
      "Start Time (12 hours) & Date",
      "End Time (12 hours) & Date",
      "Total Setup (hrs)",
      "Total Production (hrs)",
      "Production Qty."
    ];

    // Add InputQty and PassQty to header if selectedMachine starts with "QCM"
    if (selectedMachine.startsWith("QCM")) {
      jobSummaryHeader.push("Input Qty.", "Pass Qty");
    }

    // Add remaining common headers
    jobSummaryHeader.push(
      "Total Idle (hrs)",
      "Total Meals (hrs)",
      "Idle Reasons",
      "Job Close Remarks",
      "Finish Good",
      "Status"
    );


    const headerRow = jobSummaryWorksheet.addRow(jobSummaryHeader);

    // Apply a color to the headers in the "Job Summary" sheet
    headerRow.eachCell(cell => {
      cell.font = { bold: true, color: { argb: '000000' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFCC' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'F79646' } },
        left: { style: 'thin', color: { argb: 'F79646' } },
        bottom: { style: 'thin', color: { argb: 'F79646' } },
        right: { style: 'thin', color: { argb: 'F79646' } },
      };
    });


    jobSummary.forEach(job => {
      const row = [
        job.Employee_ID,
        job.name,
        job.machineCode,
        job.jobNo,
        job.customer,
        job.product,
        job.mainOperation,
        dayjs(job.startDatetime).format("MM/DD/YYYY hh:mm A"),
        dayjs(job.endDatetime).format("MM/DD/YYYY hh:mm A"),
        parseFloat(timeToDecimalHours(job.totalSetupTime)),
        parseFloat(timeToDecimalHours(job.totalProductionTime)),
        job.productionQty
      ];

      // Conditionally add InputQty and PassQty if selectedMachine starts with "QCM"
      if (selectedMachine.startsWith("QCM")) {
        row.push(job.InputQty, job.PassQty);
      }

      // Add remaining common fields
      row.push(
        parseFloat(timeToDecimalHours(job.totalIdleTime)),
        parseFloat(timeToDecimalHours(job.totalMealsTime)),
        job.idleReasons,
        job.jobCloseremarks,
        job.finishedGood,
        job.status
      );

      jobSummaryWorksheet.addRow(row);
    });

    jobSummaryWorksheet.columns.forEach(column => {
      column.width = 20; // Adjust width as necessary
    });

    //////////////////////////////////

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

    const downloadLink = document.createElement("a");
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = filename;
    downloadLink.click();
  };

  // Excel Download for Manhour Operations
  const downloadManhourExcel = () => {
    console.log("=== STARTING MANHOUR EXCEL DOWNLOAD ===");

    const workbook = new ExcelJS.Workbook();

    const addWorksheet = (name, headers, data) => {
      console.log(`Adding worksheet: ${name} with ${data.length} rows`);
      const worksheet = workbook.addWorksheet(name);

      // Add header row
      const headerRow = worksheet.addRow(headers);

      // Style header row
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4472C4" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Add data rows with numeric formatting for time columns
      data.forEach((rowData) => {
        const dataRow = worksheet.addRow(rowData);

        dataRow.eachCell((cell, colNumber) => {
          // Apply borders to all cells
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          cell.alignment = { vertical: "middle", wrapText: true };

          // Format numeric cells
          if (typeof cell.value === 'number') {
            const header = headers[colNumber - 1];

            // Check if it's a time/duration column - format with 2 decimals
            if (header && (
              header.toLowerCase().includes('hours') ||
              header.toLowerCase().includes('duration') ||
              header.toLowerCase().includes('time')
            )) {
              cell.numFmt = '0.00';
            }
            // Check if it's a quantity column - format as integer
            else if (header && header.toLowerCase().includes('qty')) {
              cell.numFmt = '0';
            }
            // Check if it's a count column - format as integer
            else if (header && header.toLowerCase().includes('count')) {
              cell.numFmt = '0';
            }
            // Check if it's an ID column - format as integer
            else if (header && (header.toLowerCase().includes('id') || header === 'ID')) {
              cell.numFmt = '0';
            }
            // Default: 2 decimal places for other numbers
            else {
              cell.numFmt = '0.00';
            }

            cell.alignment = { ...cell.alignment, horizontal: 'right' };
          }
        });
      });

      // Auto-size columns
      worksheet.columns.forEach((column, index) => {
        const headerLength = headers[index]?.length || 12;
        column.width = Math.max(headerLength + 6, 15);
      });

      console.log(`✓ Worksheet ${name} added successfully`);
    };


    // Sheet 2: MAN Shift Logs
    if (manShiftLogs.length > 0) {
      console.log(">>> Creating MAN Shift Logs sheet");
      addWorksheet(
        "MAN Shift Logs",
        ["Supervisor", "Employee", "Station Code", "Shift In", "Shift Out",
          //  "Active Status"
        ],
        manShiftLogs.map((log) => [

          log.supervisor,
          log.employee,
          log.stationCode,
          log.shiftIn,
          log.shiftOut,
          // log.activeStatus,
        ])
      );
    }


    if (manDetailLogs.length > 0) {
      console.log(">>> Creating MAN Detail Logs sheet (APPROVED ONLY)");
      addWorksheet(
        "MAN Detail Logs (Approved)",
        [
          "Supervisor ID",
          "Employee ID",
          "Employee Name",
          "Station Code",
          "Job Number",
          "Customer",
          "Product",
          "Main Operation",
          "Operation Code",
          "Operation Description",
          "Start Datetime",
          "End Datetime",
          "Job Started Time",
          "Remarks",
          "Operation Type",
          "Duration",
          "Duration (hours)",
          "Prod Time (hours)",
          "Idle Time (hours)",
          "Meal Time (hours)",
          "Non-commanded (hours)",
          "Setup Time (hours)",
          "Order Qty",
          "Ups Count",
          "Production Qty",
          ...(isBreakingReport ? ["Total Carton Count"] : []),
          "Waste Qty",
          "Finished Good Status",
          "Job Status",
          "Approved At",
          "Idle Reasons",
          "Created At",
          "Updated At"
        ],
        manDetailLogs.map((l) => [
          parseFloat(l.supervisor_id) || 0,
          parseFloat(l.emp_id) || 0,
          l.emp_name,
          l.station_code,
          l.job_number,
          l.customer,
          l.product,
          l.main_operation,
          l.operation_code,
          l.operation_description,
          l.start_datetime ? dayjs(l.start_datetime).format("YYYY-MM-DD HH:mm:ss") : "",
          l.end_datetime ? dayjs(l.end_datetime).format("YYYY-MM-DD HH:mm:ss") : "",
          l.job_started_time ? dayjs(l.job_started_time).format("YYYY-MM-DD HH:mm:ss") : "",
          l.remarks || "",
          l.operation_type || "",
          l.duration || "",
          parseFloat((l.duration_hours || 0)),
          parseFloat((timeToMinutes(l.prodtime) / 60).toFixed(2)),
          parseFloat((timeToMinutes(l.idletime) / 60).toFixed(2)),
          parseFloat((timeToMinutes(l.mealtime) / 60).toFixed(2)),
          parseFloat((l.non_commanded_hours || 0).toFixed ? l.non_commanded_hours.toFixed(2) : l.non_commanded_hours) || 0,
          parseFloat((timeToMinutes(l.setup_time) / 60).toFixed(2)),
          parseFloat(l.order_qty) || 0,
          l.up_count ?? "",
          parseFloat(l.prod_qty) || 0,
          ...(isBreakingReport ? [
            calculateTotalCartonCount(l.station_code, l.prod_qty, l.up_count) ?? ""
          ] : []),
          parseFloat(l.waste_qty) || 0,
          l.finished_good_status || "",
          l.job_status || "",
          l.approved_at ? dayjs(l.approved_at).format("YYYY-MM-DD HH:mm:ss") : "",
          l.idle_reasons || "",
          l.created_at ? dayjs(l.created_at).format("YYYY-MM-DD HH:mm:ss") : "",
          l.updated_at ? dayjs(l.updated_at).format("YYYY-MM-DD HH:mm:ss") : ""
        ])
      );
    }


    if (manSummaryLogs.length > 0) {
      console.log(">>> Creating Employee-wise Job Summary sheet");
      addWorksheet(
        "Employee-wise Job Summary",
        [
          "ID", "Supervisor", "Employee", "Workstation Code", "Job Number",
          "Customer", "Product", "Main Operation",
          "Order Qty", "Ups Count", "Prod Qty",
          ...(isBreakingReport ? ["Total Carton Count"] : []),
          "Waste Qty",
          "Finished Good", "Job Started", "Job Ended",
          "Duration", "Duration (hours)",
          "Total Setup (hours)", "Total Idle (hours)", "Total Prod (hours)", "Total Meals (hours)",
          "Non-commanded (hours)", "Idle Reasons", "Status", "Remarks"
        ],
        manSummaryLogs.map(log => [
          parseFloat(log.id) || 0,
          log.supervisor,
          log.employee,
          log.workstationCode,
          log.jobNumber,
          log.customer,
          log.product,
          log.mainOperation,
          parseFloat(log.orderQty) || 0,
          log.upCount ?? "",
          parseFloat(log.prodQty) || 0,
          ...(isBreakingReport ? [parseFloat(log.totalCartonCount) || 0] : []),
          parseFloat(log.wasteQty) || 0,
          log.finishedGood,
          log.jobStarted ? dayjs(log.jobStarted).format("YYYY-MM-DD HH:mm:ss") : "",
          log.jobEnded ? dayjs(log.jobEnded).format("YYYY-MM-DD HH:mm:ss") : "",
          log.duration || "",
          parseFloat((log.durationHours || 0)),
          parseFloat((timeToMinutes(log.totalSetup) / 60).toFixed(2)),
          parseFloat((timeToMinutes(log.totalIdle) / 60).toFixed(2)),
          parseFloat((timeToMinutes(log.totalProd) / 60).toFixed(2)),
          parseFloat((timeToMinutes(log.totalMeals) / 60).toFixed(2)),
          parseFloat(log.nonCommandedHours || 0),
          log.idleReasons || "",
          log.status || "",
          log.remarks || ""
        ])
      );
    }



    if (jobSummaryGrouped.length > 0) {
      console.log(">>> Creating Job Summary (Grouped) sheet");
      addWorksheet(
        "Job Summary (Grouped)",
        [
          "ID", "Job Number", "Customer", "Product", "Main Operation",
          "Workstation Code", "Employees", "Employee Count",
          "Total Order Qty", "Ups Count", "Total Production Qty",
          ...(isBreakingReport ? ["Total Carton Count"] : []),
          "Total Waste Qty",
          "Total Duration (hours)", "Total Setup (hours)", "Total Idle (hours)",
          "Total Prod (hours)", "Total Meal (hours)", "Total Non-commanded (hours)",
          "Finished Good Status", "Job Status",
          "Earliest Start", "Latest End",
          "Idle Reasons", "Remarks"
        ],
        jobSummaryGrouped.map(s => [
          parseFloat(s.id) || 0,
          s.jobNumber,
          s.customer,
          s.product,
          s.mainOperation,
          s.workstationCode,
          s.employees,
          parseFloat(s.employeeCount) || 0,
          parseFloat(s.totalOrderQty) || 0,
          s.upCounts || "—",
          parseFloat(s.totalProdQty) || 0,
          ...(isBreakingReport ? [parseFloat(s.totalCartonCount) || 0] : []),
          parseFloat(s.totalWasteQty) || 0,
          parseFloat((s.totalDurationHours || 0)),
          parseFloat((s.totalSetupHours || 0)),
          parseFloat((s.totalIdleHours || 0)),
          parseFloat((s.totalProdHours || 0)),
          parseFloat((s.totalMealHours || 0)),
          parseFloat((s.totalNonCommandedHours || 0)),
          s.finishedGood,
          s.status,
          s.earliestJobStart ? dayjs(s.earliestJobStart).format("YYYY-MM-DD HH:mm:ss") : "",
          s.latestJobEnd ? dayjs(s.latestJobEnd).format("YYYY-MM-DD HH:mm:ss") : "",
          s.idleReasons || "",
          s.remarks || ""
        ])
      );
    }

    // Save the workbook
    console.log("\n=== SAVING WORKBOOK ===");
    workbook.xlsx
      .writeBuffer()
      .then((buffer) => {
        console.log(" Buffer created, size:", buffer.byteLength, "bytes");
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        console.log(" Blob created");
        saveAs(blob, `Manhour_Logs_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`);
        console.log(" File download initiated");
        console.log("=== EXCEL DOWNLOAD COMPLETE ===");
      })
      .catch((err) => {
        console.error("Error exporting Excel:", err);
        console.error("Error stack:", err.stack);
      });
  };

  // Calculate Statistics
  const getTotalRecords = () => {
    return manDetailLogs.length > 0 ? manDetailLogs.length : operationLogs.length;
  };

  const getCompletedJobs = () => {
    if (manDetailLogs.length > 0) {
      return manDetailLogs.filter((l) => l.job_status === "Completed").length;
    }
    return operationLogs.filter((l) => l.job_status === "Completed").length;
  };

  const getUniqueEmployees = () => {
    if (manDetailLogs.length > 0) {
      return new Set(manDetailLogs.map((l) => l.emp_id)).size;
    }
    return new Set(operationLogs.map((l) => l.employee_id)).size;
  };

  const getTotalHours = () => {
    if (manDetailLogs.length > 0) {
      return manDetailLogs.reduce((sum, l) => sum + (parseFloat(l.duration_hours) || 0), 0).toFixed(2);
    }
    return operationLogs.reduce((sum, l) => sum + (parseFloat(l.duration_hours) || 0), 0).toFixed(2);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, 
    // backgroundColor: "#f5f7fa",
     minHeight: "100vh" }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <AssignmentIcon sx={{ fontSize: 0, color: "white" }} />
          <Box>
            <Typography variant="h5" sx={{ color: "white", fontWeight: 600, mb: 0.5 }}>
              View Completed Operations
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
              Search and analyze operation logs with advanced filtering
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Filter Toggle */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setShowFilters(!showFilters)}
          sx={{ textTransform: "none" }}
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>
      </Box>

      {/* Filter Section */}
      <Collapse in={showFilters}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Time Range Card */}
          <Grid item xs={12} md={5}>
            <Card
              sx={{
                height: "100%",
                // background: alpha("#dff9f9", 0.5),
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(
                      theme.palette.info.main,
                      0.2
                    )} 100%)`,
                backdropFilter: "blur(10px)",
                border: `1px solid ${alpha("#00bcd4", 0.2)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <CalendarIcon color="info" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Time Range
                  </Typography>
                </Box>
                <Stack spacing={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      label="Start Date Time"
                      value={startTime}
                      onChange={(date) => {
                        setStartTime(date);
                        validateTimeRange(date, endTime);
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      label="End Date Time"
                      value={endTime}
                      onChange={(date) => {
                        setEndTime(date);
                        validateTimeRange(startTime, date);
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Machine Selection Card */}
          <Grid item xs={12} md={5}>
            <Card
              sx={{
                height: "100%",
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )} 100%)`,
                backdropFilter: "blur(10px)",
                border: `1px solid ${alpha("#8bc34a", 0.2)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <MachineIcon color="success" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Machine Selection
                  </Typography>
                </Box>
                <Stack spacing={2}>
                  <Tooltip title="Select the machine group to filter available machines" arrow placement="top">
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Machine Group</InputLabel>
                      <Select value={selectedGroup} onChange={handleGroupChange} label="Select Machine Group">
                        {machineGroups.map((group) => (
                          <MenuItem key={group.MachineGroupCode} value={group.MachineGroupCode}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Chip label={group.MachineGroupCode} size="small" color="primary" variant="outlined" />
                              {group.GroupDescription}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Tooltip>

                  {selectedGroup && (
                    <Fade in>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Machine Code</InputLabel>
                        <Select value={selectedMachine} onChange={handleMachineChange} label="Select Machine Code">
                          {machines.map((machine) => (
                            <MenuItem key={machine.MachineCode} value={machine.MachineCode}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Chip label={machine.MachineCode} size="small" color="success" variant="outlined" />
                                {machine.MachineDescription}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Fade>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Status Filter Card */}
          <Grid item xs={12} md={2}>
            <Card
              sx={{
                height: "100%",
                // background: alpha("#e2fdf0", 0.5),
                backdropFilter: "blur(10px)",
                border: `1px solid ${alpha("#4caf50", 0.2)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <FilterIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Status Filter
                  </Typography>
                </Box>
                <FormGroup sx={{ gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={ongoingChecked}
                        onChange={(e) => setOngoingChecked(e.target.checked)}
                        sx={{
                          "&.Mui-checked": {
                            color: theme.palette.warning.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "warning.main",
                          }}
                        />
                        <Typography variant="body2">Ongoing</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={finishedChecked}
                        onChange={(e) => setFinishedChecked(e.target.checked)}
                        sx={{
                          "&.Mui-checked": {
                            color: theme.palette.success.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "success.main",
                          }}
                        />
                        <Typography variant="body2">Finished</Typography>
                      </Box>
                    }
                  />
                </FormGroup>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Collapse>

      {/* Error Message */}
      {errorMessage && (
        <Fade in>
          <Alert
            severity="error"
            onClose={() => setErrorMessage("")}
            sx={{
              mb: 2,
              borderRadius: 2,
              "& .MuiAlert-icon": {
                fontSize: 28,
              },
            }}
            icon={<WarningIcon />}
          >
            <AlertTitle>Validation Error</AlertTitle>
            {errorMessage}
          </Alert>
        </Fade>
      )}

      {/* Action Buttons */}
      {selectedMachine && !errorMessage && (ongoingChecked || finishedChecked) && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 2,
            my: 3,
          }}
        >
          <Button
            variant="contained"
            onClick={handleViewClick}
            disabled={isSearching}
            startIcon={isSearching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
            sx={{
              minWidth: 180,
              background: "linear-gradient(45deg, #4caf50 30%, #81c784 90%)",
              color: "white",
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1rem",
              boxShadow: "0 3px 5px 2px rgba(76, 175, 80, .3)",
              "&:hover": {
                boxShadow: "0 6px 10px 4px rgba(76, 175, 80, .3)",
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: "rgba(0, 0, 0, 0.12)",
              },
            }}
          >
            {isSearching ? "Searching..." : "View Operations"}
          </Button>

          {operationLogs.length > 0 && (
            <Zoom in timeout={300}>
              <Button
                variant="outlined"
                onClick={downloadExcel}
                startIcon={<DownloadIcon />}
                sx={{
                  minWidth: 180,
                  borderWidth: 2,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  "&:hover": {
                    borderWidth: 2,
                    transform: "translateY(-2px)",
                    boxShadow: 3,
                  },
                }}
              >
                Download Excel
              </Button>
            </Zoom>
          )}

          {manDetailLogs.length > 0 && (
            <Zoom in timeout={400}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={downloadManhourExcel}
                startIcon={<DownloadIcon />}
                sx={{
                  minWidth: 180,
                  borderWidth: 2,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  "&:hover": {
                    borderWidth: 2,
                    transform: "translateY(-2px)",
                    boxShadow: 3,
                  },
                }}
              >
                Download Manhour Report
              </Button>
            </Zoom>
          )}
        </Stack>
      )}



      {/* Statistics Cards */}
      {(manDetailLogs.length > 0 || operationLogs.length > 0) && (
        <Fade in timeout={600}>
          <Box sx={{ mb: 3 }}>
            {/* Main Statistics Row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={manDetailLogsNonApproved.length > 0 ? 2.4 : 3}>
                <Card
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(
                      theme.palette.info.main,
                      0.2
                    )} 100%)`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box>
                        <Typography variant="h3" color="info.main" sx={{ fontWeight: 700 }}>
                          {getTotalRecords()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Records
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          Approved Only
                        </Typography>
                      </Box>
                      <AssignmentIcon sx={{ fontSize: 48, color: theme.palette.info.main, opacity: 0.5 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={manDetailLogsNonApproved.length > 0 ? 2.4 : 3}>
                <Card
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(
                      theme.palette.success.main,
                      0.2
                    )} 100%)`,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box>
                        <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                          {getCompletedJobs()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Completed Jobs
                        </Typography>
                      </Box>
                      <CheckCircleIcon sx={{ fontSize: 48, color: theme.palette.success.main, opacity: 0.5 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={manDetailLogsNonApproved.length > 0 ? 2.4 : 3}>
                <Card
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(
                      theme.palette.warning.main,
                      0.2
                    )} 100%)`,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box>
                        <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700 }}>
                          {getUniqueEmployees()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Employees
                        </Typography>
                      </Box>
                      <PeopleIcon sx={{ fontSize: 48, color: theme.palette.warning.main, opacity: 0.5 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={manDetailLogsNonApproved.length > 0 ? 2.4 : 3}>
                <Card
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box>
                        <Typography variant="h3" color="primary.main" sx={{ fontWeight: 700 }}>
                          {getTotalHours()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Hours
                        </Typography>
                      </Box>
                      <ScheduleIcon sx={{ fontSize: 48, color: theme.palette.primary.main, opacity: 0.5 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>



              {/* Non-Approved Card */}
              {manDetailLogsNonApproved.length > 0 && (
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card
                    onClick={handleNonApprovedCardClick}
                    sx={{
                      background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(
                        theme.palette.error.main,
                        0.2
                      )} 100%)`,
                      border: `2px solid ${alpha(theme.palette.error.main, 0.4)}`,
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box>
                          <Typography variant="h3" color="error.main" sx={{ fontWeight: 700 }}>
                            {getNonApprovedCount()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Non-Approved
                          </Typography>
                          <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
                            {getNonApprovedTotalHours()} hours
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled', fontSize: '0.7rem' }}>
                            Click to view details
                          </Typography>
                        </Box>
                        <WarningIcon sx={{ fontSize: 48, color: theme.palette.error.main, opacity: 0.5 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

          </Box>
        </Fade>
      )}



      {/* Charts Section with TABS */}
      {manDetailLogs.length > 0 && (
        <Fade in timeout={700}>
          <Box sx={{ mb: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <TrendingUpIcon sx={{ fontSize: 32, color: "primary.main" }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "text.primary" }}>
                    Analytics & Insights
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Visual representation of operation data
                  </Typography>
                </Box>
              </Box>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                  value={selectedChartTab}
                  onChange={(e, newValue) => setSelectedChartTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                    },
                  }}
                >
                  <Tab label="Time Distribution" value="timeDistribution" />
                  {getNonJobActivityBreakdownData().length > 0 && (
                    <Tab label="Non-Job Breakdown" value="nonJobBreakdown" />
                  )}
                  {getEmployeeProductivityData().length > 0 && (
                    <Tab label="Employee Productivity" value="employeeProductivity" />
                  )}
                  {getJobStatusDistributionData().length > 0 && (
                    <Tab label="Job Status" value="jobStatus" />
                  )}
                  {getProductionQtyByJobData().length > 0 && (
                    <Tab label="Production by Job" value="productionByJob" />
                  )}
                  {getHourlyTimelineData().length > 0 && (
                    <Tab label="Timeline" value="timeline" />
                  )}
                </Tabs>
              </Box>

              {/* Tab Panels */}

              {/* Time Distribution Tab */}
              {selectedChartTab === 'timeDistribution' && (
                <Box sx={{ minHeight: 400 }}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: alpha(theme.palette.background.paper, 0.6),
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                      Total Time Distribution
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={getTimeDistributionData()}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={CustomPieLabel}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {getTimeDistributionData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomPieTooltip />} />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          formatter={(value, entry) => `${value}: ${entry.payload.value.toFixed(2)} hrs`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>
                </Box>
              )}

              {/* Non-Job Activity Breakdown Tab */}
              {selectedChartTab === 'nonJobBreakdown' && getNonJobActivityBreakdownData().length > 0 && (
                <Box sx={{ minHeight: 400 }}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: alpha(theme.palette.background.paper, 0.6),
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                      Non-Job Activity Breakdown
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={getNonJobActivityBreakdownData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 12 }}
                          angle={-15}
                          textAnchor="end"
                          height={70}
                        />
                        <YAxis tick={{ fontSize: 12 }} label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="hours" radius={[8, 8, 0, 0]}>
                          {getNonJobActivityBreakdownData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Box>
              )}

              {/* Employee Productivity Tab */}
              {selectedChartTab === 'employeeProductivity' && getEmployeeProductivityData().length > 0 && (
                <Box sx={{ minHeight: 400 }}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: alpha(theme.palette.background.paper, 0.6),
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                      Top 10 Employees by Hours
                    </Typography>
                    <ResponsiveContainer width="100%" height={450}>
                      <BarChart
                        data={getEmployeeProductivityData()}
                        layout="vertical"
                        margin={{ left: 120 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          tick={{ fontSize: 10 }}
                          width={110}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="jobHours" stackId="a" fill="#4caf50" name="Job Hours" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="nonJobHours" stackId="a" fill="#ff9800" name="Non-Job Hours" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Box>
              )}

              {/* Job Status Distribution Tab */}
              {selectedChartTab === 'jobStatus' && getJobStatusDistributionData().length > 0 && (
                <Box sx={{ minHeight: 400 }}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: alpha(theme.palette.background.paper, 0.6),
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                      Job Status Distribution
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={getJobStatusDistributionData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis yAxisId="left" orientation="left" tick={{ fontSize: 11 }} label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} label={{ value: 'Hours', angle: 90, position: 'insideRight' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="count" fill="#2196f3" name="Count" radius={[8, 8, 0, 0]} />
                        <Bar yAxisId="right" dataKey="hours" fill="#ff9800" name="Hours" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Box>
              )}

              {/* Production Quantity by Job Tab */}
              {selectedChartTab === 'productionByJob' && getProductionQtyByJobData().length > 0 && (
                <Box sx={{ minHeight: 400 }}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: alpha(theme.palette.background.paper, 0.6),
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                      Top 10 Jobs by Production Quantity
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={getProductionQtyByJobData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 10 }}
                          angle={-30}
                          textAnchor="end"
                          height={90}
                        />
                        <YAxis tick={{ fontSize: 11 }} label={{ value: 'Quantity', angle: -90, position: 'insideLeft' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="prodQty" fill="#4caf50" name="Production Qty" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="wasteQty" fill="#f44336" name="Waste Qty" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Box>
              )}

              {/* Hourly Timeline Tab */}
              {selectedChartTab === 'timeline' && getHourlyTimelineData().length > 0 && (
                <Box sx={{ minHeight: 400 }}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: alpha(theme.palette.background.paper, 0.6),
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                      Activity Timeline (Hourly)
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={getHourlyTimelineData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                        <XAxis
                          dataKey="displayTime"
                          tick={{ fontSize: 9 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis tick={{ fontSize: 11 }} label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="jobHours"
                          stroke="#4caf50"
                          strokeWidth={2}
                          name="Job Hours"
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="nonJobHours"
                          stroke="#ff9800"
                          strokeWidth={2}
                          name="Non-Job Hours"
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>
                </Box>
              )}
            </Paper>
          </Box>
        </Fade>
      )}


      {manDetailLogsNonApproved.length > 0 && (
        <Fade in timeout={1100}>
          <Box sx={{ mb: 4 }} id="non-approved-table">
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                boxShadow: "0 4px 20px rgba(255,152,0,0.15)",
                background: alpha(theme.palette.warning.light, 0.02),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <WarningIcon sx={{ fontSize: 32, color: "warning.main" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "text.primary" }}>
                    Employee-wise Detail Logs (Non-Approved)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {manDetailLogsNonApproved.length} non-approved operation records
                  </Typography>
                </Box>
                <Chip
                  label="NOT APPROVED"
                  color="warning"
                  icon={<WarningIcon />}
                  sx={{ fontWeight: 600 }}
                />
                <Tooltip title={expandedTables.nonApprovedDetails ? "Collapse" : "Expand"}>
                  <Button
                    onClick={() => toggleTableExpansion('nonApprovedDetails')}
                    sx={{ minWidth: 'auto', p: 1 }}
                  >
                    {expandedTables.nonApprovedDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Button>
                </Tooltip>
              </Box>
              <Collapse in={expandedTables.nonApprovedDetails}>
                <Box sx={{ height: 550, width: "100%" }}>
                  <DataGrid
                    rows={manDetailLogsNonApproved.map(log => ({
                      ...log,
                      job_number: "",
                      customer: "",
                      product: "",
                      main_operation: "",
                      prod_qty: "",
                      waste_qty: "",
                      finished_good_status: "",
                      job_status: "",
                    }))}
                    columns={detailColumns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    pagination
                    disableSelectionOnClick
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-cell": {
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        py: 1.5,
                      },
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: alpha(theme.palette.warning.main, 0.08),
                        borderRadius: 1,
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        borderBottom: `2px solid ${theme.palette.warning.main}`,
                      },
                      "& .MuiDataGrid-row": {
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.warning.main, 0.04),
                          cursor: "pointer",
                        },
                        backgroundColor: alpha(theme.palette.warning.light, 0.02),
                      },
                      "& .MuiDataGrid-footerContainer": {
                        borderTop: `2px solid ${theme.palette.divider}`,
                        backgroundColor: alpha(theme.palette.background.default, 0.5),
                        mt: 2,
                      },
                    }}
                  />
                </Box>
              </Collapse>
              {!expandedTables.nonApprovedDetails && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Click expand to view {manDetailLogsNonApproved.length} records
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Fade>
      )}

      {/* Data Tables */}
      {/* Employee-wise Shift Logs */}
      {manShiftLogs.length > 0 && (
        <Fade in timeout={800}>
          <Box sx={{ mb: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <PeopleIcon sx={{ fontSize: 32, color: "primary.main" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "text.primary" }}>
                    Employee-wise Shift Logs
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {manShiftLogs.length} shift records found
                  </Typography>
                </Box>
                <Tooltip title={expandedTables.shiftLogs ? "Collapse" : "Expand"}>
                  <Button
                    onClick={() => toggleTableExpansion('shiftLogs')}
                    sx={{ minWidth: 'auto', p: 1 }}
                  >
                    {expandedTables.shiftLogs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Button>
                </Tooltip>
              </Box>
              <Collapse in={expandedTables.shiftLogs}>
                <Box sx={{ height: 450, width: "100%" }}>
                  <DataGrid
                    rows={manShiftLogs}
                    columns={shiftColumns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    pagination
                    disableSelectionOnClick
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-cell": {
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        py: 1.5,
                      },
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        borderRadius: 1,
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                      },
                      "& .MuiDataGrid-row": {
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          cursor: "pointer",
                        },
                        "&:nth-of-type(even)": {
                          backgroundColor: alpha(theme.palette.background.default, 0.4),
                        },
                      },
                      "& .MuiDataGrid-footerContainer": {
                        borderTop: `2px solid ${theme.palette.divider}`,
                        backgroundColor: alpha(theme.palette.background.default, 0.5),
                        mt: 2,
                      },
                    }}
                  />
                </Box>
              </Collapse>
              {!expandedTables.shiftLogs && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Click expand to view {manShiftLogs.length} records
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Fade>
      )}



      {manDetailLogs.length > 0 && (
        <Fade in timeout={1000}>
          <Box sx={{ mb: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 32, color: "success.main" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "text.primary" }}>
                    Employee-wise Detail Logs (Approved)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {manDetailLogs.length} approved operation records
                  </Typography>
                </Box>
                <Chip
                  label="APPROVED"
                  color="success"
                  icon={<CheckCircleIcon />}
                  sx={{ fontWeight: 600 }}
                />
                <Tooltip title={expandedTables.approvedDetails ? "Collapse" : "Expand"}>
                  <Button
                    onClick={() => toggleTableExpansion('approvedDetails')}
                    sx={{ minWidth: 'auto', p: 1 }}
                  >
                    {expandedTables.approvedDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Button>
                </Tooltip>
              </Box>
              <Collapse in={expandedTables.approvedDetails}>
                <Box sx={{ height: 550, width: "100%" }}>
                  <DataGrid
                    rows={manDetailLogs}
                    columns={detailColumns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    pagination
                    disableSelectionOnClick
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-cell": {
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        py: 1.5,
                      },
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: alpha(theme.palette.success.main, 0.08),
                        borderRadius: 1,
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        borderBottom: `2px solid ${theme.palette.success.main}`,
                      },
                      "& .MuiDataGrid-row": {
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.success.main, 0.04),
                          cursor: "pointer",
                        },
                      },
                      "& .MuiDataGrid-footerContainer": {
                        borderTop: `2px solid ${theme.palette.divider}`,
                        backgroundColor: alpha(theme.palette.background.default, 0.5),
                        mt: 2,
                      },
                    }}
                  />
                </Box>
              </Collapse>
              {!expandedTables.approvedDetails && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Click expand to view {manDetailLogs.length} records
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Fade>
      )}

      {jobSummaryGrouped.length > 0 && (
        <Fade in timeout={1100}>
          <Box sx={{ mb: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <TrendingUpIcon sx={{ fontSize: 32, color: "success.main" }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "text.primary" }}>
                    Job Summary (Grouped)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {jobSummaryGrouped.length} unique jobs with aggregated data
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ height: 550, width: "100%" }}>
                <DataGrid
                  rows={jobSummaryGrouped}
                  columns={summaryColumns}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  pagination
                  disableSelectionOnClick
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-cell": {
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                      py: 1.5,
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: alpha(theme.palette.success.main, 0.08),
                      borderRadius: 1,
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      borderBottom: `2px solid ${theme.palette.success.main}`,
                    },
                    "& .MuiDataGrid-row": {
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.success.main, 0.04),
                        cursor: "pointer",
                      },
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: `2px solid ${theme.palette.divider}`,
                      backgroundColor: alpha(theme.palette.background.default, 0.5),
                      mt: 2,
                    },
                  }}
                />
              </Box>
            </Paper>
          </Box>
        </Fade>
      )}

      {/* Machine Operation Logs */}
      {operationLogs.length > 0 && (
        <Fade in timeout={800}>
          <Box sx={{ mb: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <ScheduleIcon sx={{ fontSize: 32, color: "info.main" }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "text.primary" }}>
                    Machine Operation Logs
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {operationLogs.length} operation records found
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ height: 500, width: "100%" }}>
                <DataGrid
                  rows={operationLogs}
                  columns={[
                    { field: "employee_id", headerName: "Employee ID", width: 120 },
                    { field: "name", headerName: "Name", width: 120 },
                    { field: "machine_code", headerName: "Machine Code", width: 120 },
                    { field: "job_number", headerName: "Job Number", width: 120 },
                    { field: "customer", headerName: "Customer", width: 120 },
                    { field: "product", headerName: "Product", width: 120 },
                    { field: "main_operation", headerName: "Main Operation", width: 120 },
                    { field: "operation_code", headerName: "Operation Code", width: 120 },
                    { field: "operation_description", headerName: "Operation Description", width: 180 },
                    { field: "operation_type", headerName: "Operation Type", width: 120 },
                    { 
                      field: "start_datetime", 
                      headerName: "Start Datetime", 
                      width: 180,
                      renderCell: (params) => {
                        const value = params.value;
                        if (!value) return "";
                        // Value should already be formatted from fetchOperationLogs
                        return value;
                      }
                    },
                    { 
                      field: "end_datetime", 
                      headerName: "End Datetime", 
                      width: 180,
                      renderCell: (params) => {
                        const value = params.value;
                        if (!value) return "";
                        // Value should already be formatted from fetchOperationLogs
                        return value;
                      }
                    },
                    { field: "duration_minutes", headerName: "Duration (mins)", width: 120 },
                    { field: "duration_hours", headerName: "Duration (hrs)", width: 120 },
                    { 
                      field: "remarks", 
                      headerName: "Remarks", 
                      width: 200,
                      renderCell: (params) => (
                        <div style={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {params.value}
                        </div>
                      )
                    },
                    { field: "active_status", headerName: "Active Status", width: 120 },
                  ]}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  pagination
                  disableSelectionOnClick
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-cell": {
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                      py: 1.5,
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: alpha(theme.palette.info.main, 0.08),
                      borderRadius: 1,
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      borderBottom: `2px solid ${theme.palette.info.main}`,
                    },
                    "& .MuiDataGrid-row": {
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.info.main, 0.04),
                        cursor: "pointer",
                      },
                    },
                  }}
                />
              </Box>
            </Paper>
          </Box>
        </Fade>
      )}

      {/* Machine Shift Logs */}
      {shiftLogs.length > 0 && (
        <Fade in timeout={900}>
          <Box sx={{ mb: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <PeopleIcon sx={{ fontSize: 32, color: "success.main" }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "text.primary" }}>
                    Machine Shift Logs
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {shiftLogs.length} shift records found
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ height: 400, width: "100%" }}>
                <DataGrid
                  rows={shiftLogs}
                  columns={[
                    { field: "Employee_ID", headerName: "Employee ID", width: 130 },
                    { field: "machineCode", headerName: "Machine Code", width: 130 },
                    { field: "shift_loggedIn_DateTime", headerName: "Shift In", width: 180 },
                    { field: "shift_loggedOut_DateTime", headerName: "Shift Out", width: 180 },
                    { field: "operator", headerName: "Operator", width: 150 },
                    { field: "AstOperator", headerName: "Assistant Operator", width: 150 },
                    { field: "Helpers", headerName: "Helper", width: 150 },
                    { 
                      field: "remarks", 
                      headerName: "Remarks", 
                      width: 200,
                      renderCell: (params) => (
                        <div style={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {params.value}
                        </div>
                      )
                    },
                    { field: "ActiveStatus", headerName: "Active Status", width: 120 },
                  ]}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  pagination
                  disableSelectionOnClick
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-cell": {
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                      py: 1.5,
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: alpha(theme.palette.success.main, 0.08),
                      borderRadius: 1,
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      borderBottom: `2px solid ${theme.palette.success.main}`,
                    },
                    "& .MuiDataGrid-row": {
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.success.main, 0.04),
                        cursor: "pointer",
                      },
                      "&:nth-of-type(even)": {
                        backgroundColor: alpha(theme.palette.background.default, 0.4),
                      },
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: `2px solid ${theme.palette.divider}`,
                      backgroundColor: alpha(theme.palette.background.default, 0.5),
                      mt: 2,
                    },
                  }}
                />
              </Box>
            </Paper>
          </Box>
        </Fade>
      )}

      {/* Empty State */}
      {manDetailLogs.length === 0 &&
        operationLogs.length === 0 &&
        manShiftLogs.length === 0 &&
        shiftLogs.length === 0 &&
        !isSearching && (
          <Fade in>
            <Paper
              elevation={0}
              sx={{
                p: 8,
                textAlign: "center",
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.5)} 0%, ${alpha(
                  theme.palette.grey[200],
                  0.3
                )} 100%)`,
                border: `2px dashed ${alpha(theme.palette.grey[400], 0.5)}`,
              }}
            >
              <InfoIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2, opacity: 0.5 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                No Operations Found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Try adjusting your filters or time range to see results
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  mt: 3,
                }}
              >
                <Typography variant="body2" color="text.disabled">
                  Suggestions:
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Expand your time range
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Select a different machine
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Check both Ongoing and Finished status filters
                </Typography>
              </Box>
            </Paper>
          </Fade>
        )}
    </Box>
  );
}